import {
  Avatar, Box, Button, Card, CardContent, Chip, Divider, Grid, Stack, Typography,
} from '@mui/material';
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import BlockIcon from '@mui/icons-material/Block';
import GavelIcon from '@mui/icons-material/Gavel';
import VerifiedIcon from '@mui/icons-material/Verified';
import DescriptionIcon from '@mui/icons-material/Description';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { practice } from '@/api/endpoints';
import { api } from '@/api/client';
import { formatDate, formatDateTime } from '@/utils/format';

type ClinicianDetail = {
  id: number;
  display_name: string;
  email?: string;
  photo: string | null;
  bio: string;
  specialties: { id: number; name: string }[];
  verification_status: 'pending' | 'in_review' | 'approved' | 'rejected' | 'suspended';
  verification_submitted_at: string | null;
  verification_decided_at: string | null;
  verification_decided_by: number | null;
  verification_notes: string;
  proof_documents: Array<{
    id: number; kind: string; filename: string; url: string;
    uploaded_at: string;
  }>;
  licenses: Array<{
    id: number; license_number: string; jurisdiction_code: string;
    license_type: string; issued_on: string; valid_to: string | null;
    can_prescribe: boolean; status: string;
  }>;
  trust_score: string | null;
  is_listed: boolean;
};

export function PracticeClinicianDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const [rejectOpen, setRejectOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);

  const q = useApiQuery({
    queryKey: ['practice', 'clinicians', id],
    queryFn:  () => api.get<ClinicianDetail>(`/api/medical/practice/clinicians/${id}/`),
    enabled:  Boolean(id),
  });

  const approveMut = useApiMutation({
    mutationFn: () => practice.clinicians.verify(parseInt(id, 10), 'approved'),
    onSuccess: () => {
      toast.success('Verification approved.');
      qc.invalidateQueries({ queryKey: ['practice', 'clinicians'] });
    },
    onError: (e) => toast.error(e.message),
  });
  const rejectMut = useApiMutation({
    mutationFn: (reason: string) =>
      practice.clinicians.verify(parseInt(id, 10), 'rejected', reason),
    onSuccess: () => {
      toast.success('Verification rejected.');
      qc.invalidateQueries({ queryKey: ['practice', 'clinicians'] });
      setRejectOpen(false);
    },
    onError: (e) => toast.error(e.message),
  });
  const deactivateMut = useApiMutation({
    mutationFn: (reason: string) =>
      practice.clinicians.deactivate(parseInt(id, 10), reason),
    onSuccess: () => {
      toast.success('Clinician deactivated.');
      qc.invalidateQueries({ queryKey: ['practice', 'clinicians'] });
      setDeactivateOpen(false);
      nav('/practice/clinicians');
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <>
      <PageHeader
        title="Clinician"
        breadcrumbs={[
          { label: 'Clinicians', to: '/practice/clinicians' },
          { label: 'Detail' },
        ]}
      />

      <DataState query={q}>
        {(c) => (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <ProfileCard clinician={c} />
              <ProofDocumentsCard documents={c.proof_documents ?? []} />
              <LicensesCard licenses={c.licenses ?? []} />
            </Grid>
            <Grid item xs={12} md={4}>
              <DecisionCard
                clinician={c}
                onApprove={() => approveMut.mutate()}
                onReject={() => setRejectOpen(true)}
                onDeactivate={() => setDeactivateOpen(true)}
                approveBusy={approveMut.isPending}
              />
            </Grid>
          </Grid>
        )}
      </DataState>

      <ConfirmDialog
        open={rejectOpen}
        title="Reject verification?"
        description="The clinician is notified and may resubmit. Reason is shown to them."
        destructive
        reasonRequired
        confirmLabel="Reject"
        onCancel={() => setRejectOpen(false)}
        onConfirm={(reason) => rejectMut.mutateAsync(reason ?? '')}
      />
      <ConfirmDialog
        open={deactivateOpen}
        title="Deactivate clinician?"
        description="The clinician is removed from search and cannot accept new appointments. Existing appointments are not affected."
        destructive
        reasonRequired
        confirmLabel="Deactivate"
        onCancel={() => setDeactivateOpen(false)}
        onConfirm={(reason) => deactivateMut.mutateAsync(reason ?? '')}
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function ProfileCard({ clinician }: { clinician: ClinicianDetail }) {
  const initials = (clinician.display_name || '?')
    .split(' ').map((p) => p[0] ?? '').join('').slice(0, 2);
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={2}>
          <Avatar src={clinician.photo ?? undefined}
                  sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: '1.5rem' }}>
            {initials}
          </Avatar>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="h5" component="h2">
              {clinician.display_name}
            </Typography>
            {clinician.email && (
              <Typography variant="body2" color="text.secondary"
                          sx={{ mt: 0.5 }}>
                {clinician.email}
              </Typography>
            )}
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
              {clinician.specialties.map((s) => (
                <Chip key={s.id} size="small" label={s.name} variant="outlined" />
              ))}
            </Stack>
          </Box>
          {clinician.trust_score && (
            <Box sx={{ textAlign: 'center', minWidth: 100 }}>
              <Typography variant="caption" color="text.secondary"
                          sx={{ display: 'block' }}>
                trust score
              </Typography>
              <Typography variant="h3" component="div" className="tabular"
                          color="primary.main">
                {Math.round(parseFloat(clinician.trust_score))}
              </Typography>
            </Box>
          )}
        </Stack>
        {clinician.bio && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.subtle', borderRadius: 1.5 }}>
            <Typography variant="body2">{clinician.bio}</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function ProofDocumentsCard({
  documents,
}: { documents: ClinicianDetail['proof_documents'] }) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary"
                    sx={{ display: 'block', mb: 1.5 }}>
          Proof documents ({documents.length})
        </Typography>
        {documents.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No proof documents uploaded yet.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {documents.map((d) => (
              <Stack key={d.id} direction="row" alignItems="center"
                     justifyContent="space-between" sx={{ p: 1, borderRadius: 1.5,
                     bgcolor: 'background.subtle' }}>
                <Stack direction="row" spacing={1.5} alignItems="center"
                       sx={{ minWidth: 0 }}>
                  <DescriptionIcon color="primary" />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={500} noWrap>
                      {d.filename}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {d.kind.replace(/_/g, ' ')} · uploaded {formatDate(d.uploaded_at)}
                    </Typography>
                  </Box>
                </Stack>
                <Button size="small" href={d.url} target="_blank" rel="noopener">
                  Open
                </Button>
              </Stack>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function LicensesCard({ licenses }: { licenses: ClinicianDetail['licenses'] }) {
  if (licenses.length === 0) return null;
  return (
    <Card>
      <CardContent>
        <Typography variant="overline" color="text.secondary"
                    sx={{ display: 'block', mb: 1.5 }}>
          Licenses ({licenses.length})
        </Typography>
        <Stack spacing={1.5} divider={<Divider />}>
          {licenses.map((l) => (
            <Stack key={l.id} direction="row" spacing={2} alignItems="center">
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: 'background.subtle', color: 'primary.main',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <GavelIcon fontSize="small" />
              </Box>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body1" fontWeight={500} className="tabular">
                    {l.license_number}
                  </Typography>
                  <Chip size="small"
                        label={l.status}
                        color={l.status === 'active' ? 'success' : 'default'}
                        variant="outlined" />
                  {l.can_prescribe && (
                    <Chip size="small" label="Rx" color="primary" variant="outlined" />
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary"
                            className="tabular">
                  {l.jurisdiction_code} · {l.license_type.replace(/_/g, ' ')}
                  {l.issued_on && ` · issued ${formatDate(l.issued_on)}`}
                  {l.valid_to && ` · valid until ${formatDate(l.valid_to)}`}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

function DecisionCard({
  clinician, onApprove, onReject, onDeactivate, approveBusy,
}: {
  clinician: ClinicianDetail;
  onApprove: () => void;
  onReject: () => void;
  onDeactivate: () => void;
  approveBusy: boolean;
}) {
  const pending = clinician.verification_status === 'pending'
    || clinician.verification_status === 'in_review';
  const approved = clinician.verification_status === 'approved';
  const rejected = clinician.verification_status === 'rejected';
  const suspended = clinician.verification_status === 'suspended';

  return (
    <Card sx={{ mb: 3, borderColor: pending ? 'warning.main' : 'divider' }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary"
                    sx={{ display: 'block', mb: 1.5 }}>
          Verification
        </Typography>

        <Stack spacing={1} sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              icon={
                approved ? <VerifiedIcon /> :
                rejected || suspended ? <CloseIcon /> :
                <CheckIcon />
              }
              label={clinician.verification_status.replace(/_/g, ' ')}
              color={
                approved ? 'success' :
                pending ? 'warning' :
                rejected ? 'error' :
                'default'
              }
              variant="outlined"
              sx={{ textTransform: 'capitalize' }}
            />
          </Stack>
          {clinician.verification_submitted_at && (
            <Typography variant="caption" color="text.secondary">
              submitted {formatDateTime(clinician.verification_submitted_at)}
            </Typography>
          )}
          {clinician.verification_decided_at && (
            <Typography variant="caption" color="text.secondary">
              decided {formatDateTime(clinician.verification_decided_at)}
            </Typography>
          )}
          {clinician.verification_notes && (
            <Box sx={{ p: 1.5, bgcolor: 'background.subtle', borderRadius: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Notes: {clinician.verification_notes}
              </Typography>
            </Box>
          )}
        </Stack>

        {pending && (
          <Stack spacing={1}>
            <Button variant="contained" color="success" fullWidth
                    startIcon={<CheckIcon />}
                    onClick={onApprove} disabled={approveBusy}>
              Approve
            </Button>
            <Button variant="outlined" color="error" fullWidth
                    startIcon={<CloseIcon />}
                    onClick={onReject}>
              Reject
            </Button>
          </Stack>
        )}

        {approved && (
          <Button variant="outlined" color="error" fullWidth
                  startIcon={<BlockIcon />}
                  onClick={onDeactivate}>
            Deactivate
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
